import { useState, useEffect, useCallback, useRef } from "react";
import { Link } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import { 
  SiEbay, 
  SiFacebook, 
  SiTiktok,
  SiInstagram
} from "react-icons/si";
import { 
  Share2, 
  Briefcase, 
  FileText, 
  ClipboardCheck, 
  ChevronRight,
  Clock,
  MessageCircle,
  Send,
  X,
  User,
  Mail,
  CheckCircle,
  Radio,
  CalendarClock
} from "lucide-react";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import axios from "axios";
import { useGoogleReCaptcha } from "react-google-recaptcha-v3";
import { useHaptics } from "@/hooks/useHaptics";

const LOGO_URL = process.env.REACT_APP_LOGO_URL;
const TIKTOK_URL = process.env.REACT_APP_TIKTOK_URL;
// Website URL for QR code - will be updated to dynamic app store link after app launch
const WEBSITE_URL = 'https://thrifty-curator.com';
const QR_CODE_URL = `https://api.qrserver.com/v1/create-qr-code/?size=150x150&data=${encodeURIComponent(WEBSITE_URL)}`;

// Custom SVG Icons for platforms - stylized representations
const PoshmarkIcon = () => (
  <svg viewBox="0 0 24 24" className="w-5 h-5" fill="white">
    <path d="M7 6h5c2.2 0 4 1.8 4 4s-1.8 4-4 4H9v4H7V6zm2 6h3c1.1 0 2-.9 2-2s-.9-2-2-2H9v4z"/>
  </svg>
);

const MercariIcon = () => (
  <svg viewBox="0 0 24 24" className="w-5 h-5" fill="white">
    <path d="M3 6h3l3 12h2l3-12h3l3 12h2V6h2v14H3V6zm5.5 0l2.5 10 2.5-10h-5z"/>
  </svg>
);

const DepopIcon = () => (
  <svg viewBox="0 0 24 24" className="w-5 h-5" fill="white">
    <path d="M12 3c-5 0-9 4-9 9s4 9 9 9 9-4 9-9-4-9-9-9zm0 14c-2.8 0-5-2.2-5-5s2.2-5 5-5 5 2.2 5 5-2.2 5-5 5z"/>
  </svg>
);

const WhatnotIcon = () => (
  <svg viewBox="0 0 115.16 115.16" className="w-full h-full">
    <path d="M115.14,57.57c0,44.51-13.06,57.57-57.57,57.57S0,102.08,0,57.57,13.06,0,57.57,0s57.57,13.06,57.57,57.57" fill="#FFE505"/>
    <path d="M57.58 0C13.06 0 0 13.06 0 57.58s13.06 57.58 57.58 57.58 57.58-13.06 57.58-57.58S102.1 0 57.58 0ZM89.5 65l-3.35 5.24C79.19 81.12 75.4 84.21 70.81 84.21c-4.85 0-7.49-2.38-13.97-12.87l-3.79-6.13c-.79-1.32-2.2-.75-1.85.75l.66 2.69c2.34 9.47-1.15 15.56-7.01 15.56-4.54 0-8.15-2.51-16-14.41l-3.26-4.94c-4.98-7.54-6.83-11.46-6.83-16.57 0-6.43 4.67-11.68 11.85-11.68 5.51 0 9.03 2.47 12.12 8.5l.79 1.54c.62 1.15 1.67 1.06 2.07 0l.57-1.5c2.2-5.77 5.77-8.55 11.37-8.55s9.34 2.78 11.5 8.55l.57 1.54c.4 1.01 1.54 1.1 2.07 0l.79-1.59c3.04-6.08 6.48-8.5 11.99-8.5 7.18 0 11.9 5.24 11.9 11.68 0 5.11-1.98 9.03-6.87 16.7Z" fill="#292527"/>
  </svg>
);

// Platform data with brand logos
const platforms = [
  { 
    name: "eBay", 
    logoUrl: process.env.REACT_APP_EBAY_LOGO_URL,
    url: "https://www.ebay.com/str/thriftycurator", 
    color: "#FFFFFF",
    useImage: true
  },
  { 
    name: "Poshmark", 
    Icon: PoshmarkIcon,
    url: "https://posh.mk/dZSDIxRJJ0b", 
    color: "#7F0353"
  },
  { 
    name: "Mercari", 
    logoUrl: process.env.REACT_APP_MERCARI_LOGO_URL,
    url: "https://www.mercari.com/u/thriftycurator/?sv=0", 
    color: "#6B7AED",
    useImage: true
  },
  { 
    name: "Depop", 
    logoUrl: process.env.REACT_APP_DEPOP_LOGO_URL,
    url: "https://www.depop.com/thriftycurator/", 
    color: "#E60023",
    useImage: true
  },
  { 
    name: "Marketplace", 
    logoUrl: process.env.REACT_APP_FB_MARKETPLACE_LOGO_URL,
    url: "https://www.facebook.com/marketplace/profile/517375094/", 
    color: "#3b5998",
    useImage: true
  },
  { 
    name: "Whatnot", 
    Icon: WhatnotIcon,
    url: "https://www.whatnot.com/user/thriftycurator", 
    color: "#FFE505",
    isWhatnot: true,
    useIcon: true
  },
];

// Form links
const formLinks = [
  { name: "Job Application", icon: Briefcase, path: "/job-application", accent: "cyan" },
  { name: "Consignment", icon: ClipboardCheck, path: "/consignment-agreement", accent: "purple" },
];

// Connect links
const connectLinks = [
  { name: "TikTok", logoUrl: process.env.REACT_APP_TIKTOK_LOGO_URL, url: TIKTOK_URL, color: "#000000", useImage: true },
  { name: "Facebook", logoUrl: process.env.REACT_APP_FB_LOGO_URL, url: "https://www.facebook.com/people/Thrifty-Curator/100070158913020/", color: "#3b5998", useImage: true },
  { name: "Instagram", url: "https://www.instagram.com/thrifty_curator/", color: "#E1306C", isInstagram: true },
  { name: "Message Us", icon: MessageCircle, isMessaging: true, color: "#FF1493" },
];

const API = process.env.REACT_APP_BACKEND_URL;

// Check if running in Capacitor (mobile app)
const isCapacitor = typeof window !== 'undefined' && window.Capacitor !== undefined;

export default function LandingPage() {
  const [shareLoading, setShareLoading] = useState(false);
  const [upcomingShow, setUpcomingShow] = useState(null);
  
  // Haptic feedback
  const { buttonPress, lightTap, successFeedback, errorFeedback } = useHaptics();

  // Fetch upcoming Whatnot show
  useEffect(() => {
    const fetchUpcoming = async () => {
      try {
        const res = await axios.get(`${API}/api/whatnot-shows/upcoming`);
        if (res.data) setUpcomingShow(res.data);
      } catch (err) {
        // silently fail — no show to display
      }
    };
    fetchUpcoming();
    // Refresh every 5 minutes
    const interval = setInterval(fetchUpcoming, 5 * 60 * 1000);
    return () => clearInterval(interval);
  }, []);
  
  // reCAPTCHA hook - always call it (hook rules), but may return null executeRecaptcha
  const { executeRecaptcha } = useGoogleReCaptcha();

  // Messaging state
  const [showMessageModal, setShowMessageModal] = useState(false);
  const [messageSent, setMessageSent] = useState(false);
  const [messageForm, setMessageForm] = useState({
    name: "",
    email: "",
    message: "",
    website: ""  // Honeypot field - should stay empty
  });
  const [sendingMessage, setSendingMessage] = useState(false);

  const handleSendMessage = async () => {
    // Validate form
    if (!messageForm.name.trim()) {
      toast.error("Please enter your name");
      return;
    }
    if (!messageForm.email.trim()) {
      toast.error("Please enter your email address");
      return;
    }
    // Basic email validation
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    if (!emailRegex.test(messageForm.email.trim())) {
      toast.error("Please enter a valid email address");
      return;
    }
    if (!messageForm.message.trim()) {
      toast.error("Please enter a message");
      return;
    }
    
    setSendingMessage(true);
    try {
      // Get reCAPTCHA token if available (web only, not mobile)
      let recaptchaToken = null;
      if (executeRecaptcha && !isCapacitor) {
        try {
          recaptchaToken = await executeRecaptcha('contact_form_submit');
        } catch (recaptchaError) {
          console.warn("reCAPTCHA failed, proceeding without token:", recaptchaError);
        }
      }
      
      await axios.post(`${API}/api/messages`, {
        sender_name: messageForm.name.trim(),
        sender_email: messageForm.email.trim().toLowerCase(),
        message: messageForm.message.trim(),
        website: messageForm.website,  // Honeypot field
        recaptcha_token: recaptchaToken  // reCAPTCHA token (null for mobile)
      });
      
      setMessageSent(true);
      toast.success("Message sent successfully!");
    } catch (error) {
      console.error("Error sending message:", error);
      if (error.response?.status === 429) {
        toast.error("Too many messages. Please wait a few minutes before trying again.");
      } else if (error.response?.status === 400) {
        toast.error("Security verification failed. Please refresh and try again.");
      } else {
        toast.error("Failed to send message. Please try again.");
      }
    } finally {
      setSendingMessage(false);
    }
  };

  const handleOpenMessaging = () => {
    setShowMessageModal(true);
    setMessageSent(false);
    setMessageForm({ name: "", email: "", message: "", website: "" });
  };

  const handleCloseMessaging = () => {
    setShowMessageModal(false);
    setMessageSent(false);
    setMessageForm({ name: "", email: "", message: "", website: "" });
  };

  const handleShare = async () => {
    lightTap(); // Haptic on share button
    setShareLoading(true);
    const shareData = {
      title: "Thrifty Curator™",
      text: "Check out Thrifty Curator™ - Curated resale finds!",
      url: WEBSITE_URL
    };

    try {
      if (navigator.share) {
        await navigator.share(shareData);
        successFeedback();
        toast.success("Thanks for sharing!");
      } else {
        await navigator.clipboard.writeText(WEBSITE_URL);
        successFeedback();
        toast.success("Link copied to clipboard!");
      }
    } catch (err) {
      if (err.name !== "AbortError") {
        errorFeedback();
        toast.error("Failed to share");
      }
    } finally {
      setShareLoading(false);
    }
  };

  const getAccentGradient = (accent) => {
    switch(accent) {
      case 'cyan': return 'linear-gradient(90deg, #00D4FF 0%, #00A8CC 100%)';
      case 'pink': return 'linear-gradient(90deg, #FF1493 0%, #E91E8C 100%)';
      case 'purple': return 'linear-gradient(90deg, #8B5CF6 0%, #6D28D9 100%)';
      default: return 'linear-gradient(90deg, #00D4FF 0%, #8B5CF6 100%)';
    }
  };

  const formatShowTime = (isoString) => {
    const date = new Date(isoString);
    const now = new Date();
    const diffMs = date - now;

    const timeStr = date.toLocaleTimeString([], { hour: 'numeric', minute: '2-digit' });

    if (diffMs < 0) return "Live Now";
    if (diffMs < 60 * 60 * 1000) {
      const mins = Math.round(diffMs / (1000 * 60));
      return `Starting in ${mins} min`;
    }

    // Compare calendar dates in user's local timezone
    const today = new Date(now.getFullYear(), now.getMonth(), now.getDate());
    const showDay = new Date(date.getFullYear(), date.getMonth(), date.getDate());
    const dayDiff = Math.round((showDay - today) / (1000 * 60 * 60 * 24));

    if (dayDiff === 0) return `Today, ${timeStr}`;
    if (dayDiff === 1) return `Tomorrow, ${timeStr}`;
    return date.toLocaleDateString([], { weekday: 'short', month: 'short', day: 'numeric' }) + `, ${timeStr}`;
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-[#1A1A2E] via-[#16213E] to-[#0F3460]" data-testid="landing-page">
      <div className="max-w-5xl mx-auto px-4 pt-16 pb-8 sm:pt-12">
        {/* Header with Logo */}
        <div className="flex flex-col items-center gap-4 mb-10">
          <div 
            className="w-28 h-28 rounded-2xl overflow-hidden shadow-2xl ring-4 ring-white/20"
            data-testid="logo-container"
          >
            <img 
              src={LOGO_URL} 
              alt="Thrifty Curator Logo" 
              className="w-full h-full object-cover" 
            />
          </div>
          <div className="text-center">
            <h1 className="font-poppins text-3xl md:text-4xl font-bold text-white tracking-tight" data-testid="main-title">
              Thrifty Curator<sup className="text-xs align-super opacity-50">™</sup>
            </h1>
            <p className="text-sm text-white/60 font-medium tracking-wider uppercase mt-1">
              Curated Resale Finds
            </p>
          </div>
        </div>

        {/* Whatnot Show Banner */}
        {upcomingShow && (
          <motion.a
            href={upcomingShow.whatnot_url || "https://www.whatnot.com/user/thriftycurator"}
            target="_blank"
            rel="noopener noreferrer"
            onClick={() => lightTap()}
            initial={{ opacity: 0, y: -10 }}
            animate={{ opacity: 1, y: 0 }}
            className="block mb-8 rounded-xl overflow-hidden shadow-2xl group cursor-pointer"
            data-testid="whatnot-show-banner"
          >
            <div className={`relative p-4 sm:p-5 ${upcomingShow.is_live
              ? 'bg-gradient-to-r from-red-600 via-red-500 to-orange-500'
              : 'bg-[#FFE505]'
            }`}>
              {/* Animated pulse for live shows */}
              {upcomingShow.is_live && (
                <div className="absolute inset-0 bg-gradient-to-r from-red-600/50 to-orange-500/50 animate-pulse" />
              )}
              <div className="relative flex items-center justify-between gap-4">
                <div className="flex items-center gap-3 min-w-0">
                  <div className={`flex-shrink-0 w-11 h-11 rounded-xl overflow-hidden flex items-center justify-center ${
                    upcomingShow.is_live ? 'bg-white/25' : 'bg-[#292527]'
                  }`}>
                    {upcomingShow.is_live ? (
                      <Radio className="w-5 h-5 text-white animate-pulse" />
                    ) : (
                      <WhatnotIcon />
                    )}
                  </div>
                  <div className="min-w-0">
                    <div className="flex items-center gap-2 flex-wrap">
                      <span className={`font-poppins font-bold text-base sm:text-lg truncate ${
                        upcomingShow.is_live ? 'text-white' : 'text-[#292527]'
                      }`}>
                        {upcomingShow.is_live ? "LIVE NOW" : "Upcoming Show"}
                      </span>
                      {upcomingShow.is_live && (
                        <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full bg-white/25 text-white text-xs font-bold uppercase tracking-wide">
                          <span className="w-1.5 h-1.5 rounded-full bg-white animate-ping" />
                          Live
                        </span>
                      )}
                    </div>
                    <p className={`text-sm font-semibold mt-0.5 ${
                      upcomingShow.is_live ? 'text-white/90' : 'text-[#292527]/80'
                    }`}>
                      {upcomingShow.title}
                    </p>
                    {upcomingShow.description && (
                      <p className={`text-xs mt-0.5 truncate ${
                        upcomingShow.is_live ? 'text-white/70' : 'text-[#292527]/60'
                      }`}>{upcomingShow.description}</p>
                    )}
                  </div>
                </div>
                <div className="flex-shrink-0 text-right">
                  <div className={`font-poppins font-bold text-sm sm:text-base ${
                    upcomingShow.is_live ? 'text-white' : 'text-[#292527]'
                  }`}>
                    {formatShowTime(upcomingShow.scheduled_at)}
                  </div>
                  <div className={`text-xs font-medium mt-0.5 transition-colors ${
                    upcomingShow.is_live ? 'text-white/80 group-hover:text-white' : 'text-[#292527]/60 group-hover:text-[#292527]/90'
                  }`}>
                    Tap to watch on Whatnot →
                  </div>
                </div>
              </div>
            </div>
          </motion.a>
        )}

        {/* Main Content Grid */}
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
          {/* Left Column */}
          <div className="space-y-6">
            {/* Shop Our Stores */}
            <div className="bg-white rounded-xl overflow-hidden shadow-xl">
              <div className="h-1.5 bg-gradient-to-r from-[#00D4FF] to-[#00A8CC]" />
              <div className="p-5">
                <h2 className="font-poppins font-bold text-lg text-[#1A1A2E] mb-4" data-testid="shop-section-title">
                  Shop Our Stores
                </h2>
                <div className="space-y-3">
                  {platforms.map((platform, index) => (
                    <a
                      key={platform.name}
                      href={platform.isWhatnot && upcomingShow?.whatnot_url ? upcomingShow.whatnot_url : platform.url}
                      onClick={() => lightTap()}
                      target="_blank"
                      rel="noopener noreferrer"
                      className={`flex items-center justify-between p-3 rounded-lg transition-all duration-300 group ${
                        platform.isWhatnot && upcomingShow
                          ? 'bg-gradient-to-r from-yellow-50 to-amber-50 ring-1 ring-yellow-300'
                          : 'bg-gray-50 hover:bg-gradient-to-r hover:from-[#00D4FF]/10 hover:to-[#8B5CF6]/10'
                      }`}
                      data-testid={`platform-link-${platform.name.toLowerCase().replace(/\s+/g, '-')}`}
                    >
                      <div className="flex items-center gap-3">
                        <div 
                          className={`w-10 h-10 rounded-lg flex items-center justify-center text-white overflow-hidden ${
                            platform.useIcon ? '' : ''
                          }`}
                          style={platform.useIcon ? {} : { backgroundColor: platform.color }}
                        >
                          {platform.useImage ? (
                            <img src={platform.logoUrl} alt={platform.name} className="w-full h-full object-cover" />
                          ) : platform.useIcon ? (
                            <platform.Icon />
                          ) : (
                            <platform.Icon />
                          )}
                        </div>
                        <div>
                          <span className={`font-semibold transition-colors ${
                            platform.isWhatnot && upcomingShow
                              ? 'text-[#292527]'
                              : 'text-[#1A1A2E] group-hover:text-[#00D4FF]'
                          }`}>
                            {platform.name}
                          </span>
                          {platform.isWhatnot && upcomingShow && (
                            <div className="flex items-center gap-1 mt-0.5">
                              {upcomingShow.is_live ? (
                                <span className="inline-flex items-center gap-1 text-xs font-bold text-red-500">
                                  <span className="w-1.5 h-1.5 rounded-full bg-red-500 animate-pulse" />
                                  LIVE NOW
                                </span>
                              ) : (
                                <span className="text-xs font-medium text-[#292527]/70">
                                  {formatShowTime(upcomingShow.scheduled_at)}
                                </span>
                              )}
                            </div>
                          )}
                        </div>
                      </div>
                      <ChevronRight className={`w-5 h-5 group-hover:translate-x-1 transition-all ${
                        platform.isWhatnot && upcomingShow
                          ? 'text-[#292527]/50'
                          : 'text-gray-400 group-hover:text-[#00D4FF]'
                      }`} />
                    </a>
                  ))}
                </div>
              </div>
            </div>

            {/* Connect */}
            <div className="bg-white rounded-xl overflow-hidden shadow-xl">
              <div className="h-1.5 bg-gradient-to-r from-[#FF1493] to-[#E91E8C]" />
              <div className="p-5">
                <h2 className="font-poppins font-bold text-lg text-[#1A1A2E] mb-4" data-testid="connect-section-title">
                  Connect
                </h2>
                <div className="space-y-3">
                  {connectLinks.map((link) => (
                    link.isMessaging ? (
                      <button
                        key={link.name}
                        onClick={() => {
                          lightTap();
                          handleOpenMessaging();
                        }}
                        className="w-full flex items-center justify-between p-3 rounded-lg bg-gray-50 hover:bg-gradient-to-r hover:from-[#FF1493]/10 hover:to-[#8B5CF6]/10 transition-all duration-300 group text-left"
                        data-testid={`connect-${link.name.toLowerCase().replace(/\s+/g, '-')}`}
                      >
                        <div className="flex items-center gap-3">
                          <div 
                            className="w-10 h-10 rounded-lg flex items-center justify-center overflow-hidden"
                            style={{ backgroundColor: link.color }}
                          >
                            <link.icon className="w-5 h-5 text-white" />
                          </div>
                          <span className="font-semibold text-[#1A1A2E] group-hover:text-[#FF1493] transition-colors">
                            {link.name}
                          </span>
                        </div>
                        <ChevronRight className="w-5 h-5 text-gray-400 group-hover:text-[#FF1493] group-hover:translate-x-1 transition-all" />
                      </button>
                    ) : (
                      <a
                        key={link.name}
                        href={link.url}
                        onClick={() => lightTap()}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="flex items-center justify-between p-3 rounded-lg bg-gray-50 hover:bg-gradient-to-r hover:from-[#FF1493]/10 hover:to-[#8B5CF6]/10 transition-all duration-300 group"
                        data-testid={`connect-${link.name.toLowerCase().replace(/\s+/g, '-')}`}
                      >
                        <div className="flex items-center gap-3">
                          <div 
                            className="w-10 h-10 rounded-lg flex items-center justify-center overflow-hidden"
                            style={{ backgroundColor: link.color }}
                          >
                            {link.useImage ? (
                              <img src={link.logoUrl} alt={link.name} className="w-full h-full object-cover" />
                            ) : link.isInstagram ? (
                              <SiInstagram className="w-5 h-5 text-white" />
                            ) : (
                              <link.icon className="w-5 h-5 text-white" />
                            )}
                          </div>
                          <span className="font-semibold text-[#1A1A2E] group-hover:text-[#FF1493] transition-colors">
                            {link.name}
                          </span>
                        </div>
                        <ChevronRight className="w-5 h-5 text-gray-400 group-hover:text-[#FF1493] group-hover:translate-x-1 transition-all" />
                      </a>
                    )
                  ))}
                </div>
              </div>
            </div>
          </div>

          {/* Right Column */}
          <div className="space-y-6">
            {/* Forms & Applications */}
            <div className="bg-white rounded-xl overflow-hidden shadow-xl">
              <div className="h-1.5 bg-gradient-to-r from-[#8B5CF6] to-[#6D28D9]" />
              <div className="p-5">
                <h2 className="font-poppins font-bold text-lg text-[#1A1A2E] mb-4" data-testid="forms-section-title">
                  Forms & Applications
                </h2>
                <div className="space-y-3">
                  {formLinks.map((link) => (
                    <div key={link.name}>
                      <Link
                        to={link.path}
                        onClick={() => buttonPress()}
                        className="flex items-center justify-between p-3 rounded-lg bg-gray-50 hover:bg-gradient-to-r hover:from-[#8B5CF6]/10 hover:to-[#00D4FF]/10 transition-all duration-300 group"
                        data-testid={`form-link-${link.name.toLowerCase().replace(/\s+/g, '-')}`}
                      >
                        <div className="flex items-center gap-3">
                          <div 
                            className="w-10 h-10 rounded-lg flex items-center justify-center"
                            style={{ background: getAccentGradient(link.accent) }}
                          >
                            <link.icon className="w-5 h-5 text-white" />
                          </div>
                          <span className="font-semibold text-[#1A1A2E] group-hover:text-[#8B5CF6] transition-colors">
                            {link.name}
                          </span>
                        </div>
                        <ChevronRight className="w-5 h-5 text-gray-400 group-hover:text-[#8B5CF6] group-hover:translate-x-1 transition-all" />
                      </Link>
                    </div>
                  ))}
                </div>
              </div>
            </div>

            {/* Employee Portal */}
            <div className="bg-white rounded-xl overflow-hidden shadow-xl">
              <div className="h-1.5 bg-gradient-to-r from-[#00D4FF] via-[#8B5CF6] to-[#FF1493]" />
              <div className="p-5">
                <h2 className="font-poppins font-bold text-lg text-[#1A1A2E] mb-4" data-testid="employee-section-title">
                  Employee
                </h2>
                <div>
                  <Link
                    to="/login"
                    onClick={() => buttonPress()}
                    className="flex items-center justify-between p-4 rounded-lg bg-gradient-to-r from-[#1A1A2E] to-[#16213E] hover:from-[#16213E] hover:to-[#0F3460] transition-all duration-300 group"
                    data-testid="employee-login-link"
                  >
                    <div className="flex items-center gap-3">
                      <div className="w-10 h-10 rounded-lg flex items-center justify-center bg-gradient-to-r from-[#00D4FF] to-[#8B5CF6]">
                        <Clock className="w-5 h-5 text-white" />
                      </div>
                      <span className="font-semibold text-white">
                        Employee Portal
                      </span>
                    </div>
                    <ChevronRight className="w-5 h-5 text-white/60 group-hover:text-[#00D4FF] group-hover:translate-x-1 transition-all" />
                  </Link>
                </div>
              </div>
            </div>

            {/* QR Code & Share Section */}
            <div className="bg-white rounded-xl overflow-hidden shadow-xl">
              <div className="h-1.5 bg-gradient-to-r from-[#00D4FF] via-[#8B5CF6] to-[#FF1493]" />
              <div className="p-5">
                <div className="flex flex-col items-center gap-4">
                  {/* QR Code */}
                  <div className="flex flex-col items-center gap-2" data-testid="qr-code-section">
                    <div className="p-3 bg-white rounded-xl shadow-lg ring-2 ring-gray-100" data-testid="qr-code">
                      <img 
                        src={QR_CODE_URL} 
                        alt="QR Code" 
                        className="w-24 h-24"
                      />
                    </div>
                    <p className="text-xs text-gray-500 font-medium uppercase tracking-wider">Scan to Visit</p>
                  </div>

                  {/* Share Button */}
                  <button
                    onClick={handleShare}
                    disabled={shareLoading}
                    className="w-full py-3 px-6 rounded-lg bg-gradient-to-r from-[#00D4FF] to-[#8B5CF6] text-white font-semibold hover:shadow-lg hover:shadow-[#00D4FF]/30 transition-all duration-300 flex items-center justify-center gap-2"
                    data-testid="share-button"
                  >
                    <Share2 className="w-4 h-4" />
                    {shareLoading ? "Sharing..." : "Share Thrifty Curator™"}
                  </button>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Message Us Modal */}
      <AnimatePresence>
        {showMessageModal && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="fixed inset-0 bg-black/70 backdrop-blur-sm z-50 flex items-center justify-center p-4"
            onClick={handleCloseMessaging}
            data-testid="message-modal-overlay"
          >
            <motion.div
              initial={{ opacity: 0, scale: 0.9, y: 20 }}
              animate={{ opacity: 1, scale: 1, y: 0 }}
              exit={{ opacity: 0, scale: 0.9, y: 20 }}
              transition={{ type: "spring", duration: 0.5 }}
              className="bg-gradient-to-br from-[#1A1A2E] via-[#16213E] to-[#0F3460] rounded-2xl shadow-2xl max-w-md w-full overflow-hidden border border-white/10"
              onClick={(e) => e.stopPropagation()}
              data-testid="message-modal"
            >
              {/* Modal Header */}
              <div className="bg-gradient-to-r from-[#1A1A2E] to-[#16213E] p-5 border-b border-white/10">
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-3">
                    <div className="w-10 h-10 bg-gradient-to-r from-[#FF1493] to-[#E91E8C] rounded-xl flex items-center justify-center">
                      <MessageCircle className="w-5 h-5 text-white" />
                    </div>
                    <div>
                      <h3 className="text-white font-bold text-lg">Message Us</h3>
                      <p className="text-white/60 text-sm">We'd love to hear from you</p>
                    </div>
                  </div>
                  <button
                    onClick={handleCloseMessaging}
                    className="w-8 h-8 rounded-full bg-white/10 hover:bg-white/20 flex items-center justify-center transition-colors"
                    data-testid="close-message-modal"
                  >
                    <X className="w-5 h-5 text-white" />
                  </button>
                </div>
              </div>

              {/* Modal Content */}
              <div className="p-6">
                {messageSent ? (
                  // Success State
                  <motion.div
                    initial={{ opacity: 0, y: 10 }}
                    animate={{ opacity: 1, y: 0 }}
                    className="text-center py-6"
                  >
                    <div className="w-16 h-16 bg-green-500/20 rounded-full flex items-center justify-center mx-auto mb-4">
                      <CheckCircle className="w-8 h-8 text-green-400" />
                    </div>
                    <h4 className="font-bold text-xl text-white mb-2">Message Sent!</h4>
                    <p className="text-white/70 mb-6">
                      Thank you for reaching out. We'll review your message and reply to your email shortly.
                    </p>
                    <Button
                      onClick={handleCloseMessaging}
                      className="bg-gradient-to-r from-[#00D4FF] to-[#8B5CF6] text-white hover:opacity-90"
                      data-testid="close-success-btn"
                    >
                      Close
                    </Button>
                  </motion.div>
                ) : (
                  // Form State
                  <div className="space-y-5">
                    {/* Info Banner */}
                    <div className="bg-[#00D4FF]/10 border border-[#00D4FF]/30 rounded-xl p-4">
                      <div className="flex items-start gap-3">
                        <Mail className="w-5 h-5 text-[#00D4FF] flex-shrink-0 mt-0.5" />
                        <p className="text-sm text-white/80">
                          Please provide your email address so we can respond to you. All replies will be sent directly to your inbox.
                        </p>
                      </div>
                    </div>

                    {/* Name Input */}
                    <div className="space-y-2">
                      <Label htmlFor="sender-name" className="text-white/90 font-medium">
                        Your Name <span className="text-[#FF1493]">*</span>
                      </Label>
                      <div className="relative">
                        <User className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-white/50" />
                        <Input
                          id="sender-name"
                          type="text"
                          placeholder="Enter your name"
                          value={messageForm.name}
                          onChange={(e) => setMessageForm({ ...messageForm, name: e.target.value })}
                          className="pl-10 h-12 bg-white/10 border-white/20 text-white placeholder:text-white/40 focus:border-[#00D4FF] focus:ring-[#00D4FF]/20"
                          data-testid="message-name-input"
                        />
                      </div>
                    </div>

                    {/* Email Input */}
                    <div className="space-y-2">
                      <Label htmlFor="sender-email" className="text-white/90 font-medium">
                        Your Email <span className="text-[#FF1493]">*</span>
                      </Label>
                      <div className="relative">
                        <Mail className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-white/50" />
                        <Input
                          id="sender-email"
                          type="email"
                          placeholder="Enter your email address"
                          value={messageForm.email}
                          onChange={(e) => setMessageForm({ ...messageForm, email: e.target.value })}
                          className="pl-10 h-12 bg-white/10 border-white/20 text-white placeholder:text-white/40 focus:border-[#00D4FF] focus:ring-[#00D4FF]/20"
                          data-testid="message-email-input"
                        />
                      </div>
                    </div>

                    {/* Message Input */}
                    <div className="space-y-2">
                      <Label htmlFor="message-text" className="text-white/90 font-medium">
                        Your Message <span className="text-[#FF1493]">*</span>
                      </Label>
                      <Textarea
                        id="message-text"
                        placeholder="How can we help you?"
                        value={messageForm.message}
                        onChange={(e) => setMessageForm({ ...messageForm, message: e.target.value })}
                        rows={4}
                        className="bg-white/10 border-white/20 text-white placeholder:text-white/40 focus:border-[#00D4FF] focus:ring-[#00D4FF]/20 resize-none"
                        data-testid="message-text-input"
                      />
                    </div>

                    {/* Honeypot field - hidden from real users, bots will fill it */}
                    <div className="absolute -left-[9999px] opacity-0 pointer-events-none" aria-hidden="true">
                      <label htmlFor="website">Website</label>
                      <input
                        type="text"
                        id="website"
                        name="website"
                        autoComplete="off"
                        tabIndex={-1}
                        value={messageForm.website}
                        onChange={(e) => setMessageForm({ ...messageForm, website: e.target.value })}
                      />
                    </div>

                    {/* Submit Button */}
                    <Button
                      onClick={handleSendMessage}
                      disabled={sendingMessage}
                      className="w-full h-12 bg-gradient-to-r from-[#00D4FF] to-[#8B5CF6] text-white font-semibold hover:opacity-90 transition-all"
                      data-testid="send-message-btn"
                    >
                      {sendingMessage ? (
                        <span className="flex items-center gap-2">
                          <span className="w-5 h-5 border-2 border-white/30 border-t-white rounded-full animate-spin" />
                          Sending...
                        </span>
                      ) : (
                        <span className="flex items-center gap-2">
                          <Send className="w-4 h-4" />
                          Send Message
                        </span>
                      )}
                    </Button>
                  </div>
                )}
              </div>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>
      
    </div>
  );
}
